import type { LedgerEntryType } from "../api/types";

export function formatDateTime(value: string | Date | null | undefined): string {
  if (value === null || value === undefined) {
    return "—";
  }
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  // Drop the year for in-year timestamps so the activity feed scans faster.
  // Older rows still surface the year so historical context isn't lost.
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleString(undefined, {
    ...(sameYear ? {} : { year: "numeric" }),
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit"
  });
}

export function formatDate(value: string | Date | null | undefined): string {
  if (value === null || value === undefined) {
    return "—";
  }
  const date = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(date.getTime())) {
    return "—";
  }
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric"
  });
}

export function formatNumber(value: number | bigint): string {
  return new Intl.NumberFormat().format(value);
}

export function truncateID(id: string, head = 6, tail = 4): string {
  if (id.length <= head + tail + 1) {
    return id;
  }
  return `${id.slice(0, head)}…${id.slice(-tail)}`;
}

export function prettyEntryType(type: LedgerEntryType): string {
  switch (type) {
    case "coin_grant":
      return "Coin grant";
    case "gumball_spend":
      return "Gumball spend";
    case "manual_adjustment":
      return "Adjustment";
    case "season_expiry":
      return "Season expiry";
    case "season_carryover":
      return "Carried over";
  }
}

export function displayName(name: string | null | undefined, email: string | null | undefined): string {
  const trimmedName = (name ?? "").trim();
  if (trimmedName !== "") {
    return trimmedName;
  }
  const trimmedEmail = (email ?? "").trim();
  if (trimmedEmail === "") {
    return "—";
  }
  // Prefer the local part as a display name when no name is recorded —
  // "ada.lovelace@queen.one" reads as "ada.lovelace" rather than the full
  // address. Falls back to the full email when there's no @ sign.
  const at = trimmedEmail.indexOf("@");
  if (at <= 0) {
    return trimmedEmail;
  }
  return trimmedEmail.slice(0, at);
}

export function initials(name: string, email: string): string {
  const source = name.trim() !== "" ? name : email;
  const parts = source.split(/[\s.@_-]+/).filter(Boolean);
  if (parts.length === 0) {
    return "??";
  }
  if (parts.length === 1) {
    return parts[0]!.slice(0, 2).toUpperCase();
  }
  return (parts[0]!.charAt(0) + parts[1]!.charAt(0)).toUpperCase();
}
